import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import useWindowClass from '../../hooks/useWindowClass';

/**
 * The left sub-nav inside a shell page's content area (docs/platform-standards.md
 * §13.2): a scope tab with many sections (Settings) lists them here and never as
 * a second tab row. The configuration is the same shape as a tab:
 *
 *   items: [{ key, label, to }]      `to` is a real link, so middle-click and
 *                                    copy-link work; a plain click goes through
 *                                    `onSelect` when the page keeps state.
 *
 * The active item carries `aria-current="page"`. In a compact window (§18) the list folds
 * into a disclosure above the content (a button naming the current section),
 * so nothing scrolls sideways.
 */
export default function ShellSubNav({ items = [], activeKey, onSelect, label = 'Sections', className = '' }) {
	const { compact: phone } = useWindowClass();
	const [open, setOpen] = useState(false);
	useEffect(() => { setOpen(false); }, [activeKey, phone]);
	const active = items.find(item => item.key === activeKey);

	const list = (
		<ul className="shell-subnav__list" id="shell-subnav-list">
			{items.map(item => (
				<li key={item.key}>
					<Link
						to={item.to}
						replace
						data-shell-subnav-item={item.key}
						aria-current={item.key === activeKey ? 'page' : undefined}
						className={`shell-subnav__link${item.key === activeKey ? ' shell-subnav__link--active' : ''}`}
						onClick={event => {
							if (!onSelect || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
							event.preventDefault();
							onSelect(item.key);
						}}
					>
						{item.label}
					</Link>
				</li>
			))}
		</ul>
	);

	return (
		<nav className={['shell-subnav', phone ? 'shell-subnav--phone' : '', className].filter(Boolean).join(' ')} aria-label={label} data-shell-subnav>
			{phone ? (
				<>
					<button
						type="button"
						className="shell-subnav__toggle"
						aria-expanded={open}
						aria-controls="shell-subnav-list"
						onClick={() => setOpen(value => !value)}
					>
						<span className="shell-subnav__toggle-label">{active?.label || label}</span>
						<span aria-hidden="true">{open ? '\u25B4' : '\u25BE'}</span>
					</button>
					{open ? list : null}
				</>
			) : list}
		</nav>
	);
}
